import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Play, X, CheckCheck, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { api } from "../services/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

export default function PerformanceCycles() {
  const { t } = useTranslation();

  const DEFAULT_SCALE = [
    { value: 1, label: t("performance.cycles.scale.insufficient"), color: "#ef4444" },
    { value: 2, label: t("performance.cycles.scale.toImprove"), color: "#f97316" },
    { value: 3, label: t("performance.cycles.scale.satisfactory"), color: "#eab308" },
    { value: 4, label: t("performance.cycles.scale.good"), color: "#22c55e" },
    { value: 5, label: t("performance.cycles.scale.exceptional"), color: "#0ea5e9" },
  ];

  const DEFAULT_QUESTIONS = [
    { id: "q1", text: t("performance.cycles.questions.q1"), type: "both", required: true },
    { id: "q2", text: t("performance.cycles.questions.q2"), type: "both", required: true },
    { id: "q3", text: t("performance.cycles.questions.q3"), type: "both", required: true },
    { id: "q4", text: t("performance.cycles.questions.q4"), type: "both", required: false },
    { id: "q5", text: t("performance.cycles.questions.q5"), type: "text", required: false },
    { id: "q6", text: t("performance.cycles.questions.q6"), type: "text", required: false },
  ];

  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expandedCycle, setExpandedCycle] = useState(null);

  const [form, setForm] = useState({
    name: "",
    description: "",
    type: "annual",
    start_date: "",
    end_date: "",
    rating_scale: DEFAULT_SCALE,
    questions: DEFAULT_QUESTIONS,
    salary_rules: [
      { min_score: 4.5, max_score: 5.0, increase_pct: 8 },
      { min_score: 3.5, max_score: 4.49, increase_pct: 5 },
      { min_score: 2.5, max_score: 3.49, increase_pct: 3 },
      { min_score: 0, max_score: 2.49, increase_pct: 0 },
    ],
  });

  const load = () => {
    setLoading(true);
    api.get("v1/performance/cycles/")
      .then((r) => setCycles(r.data || []))
      .catch(() => toast.error(t("performance.cycles.toasts.loadError")))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.name || !form.start_date || !form.end_date) {
      toast.error(t("performance.cycles.requiredFields"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("v1/performance/cycles/", form);
      toast.success(t("performance.cycles.toasts.created"));
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("performance.cycles.toasts.createError"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleActivate = async (id) => {
    try {
      await api.patch(`v1/performance/cycles/${id}/activate`);
      toast.success(t("performance.cycles.toasts.activated"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("performance.cycles.toasts.activateError"));
    }
  };

  const handleClose = async (id) => {
    if (!window.confirm(t("performance.cycles.confirmClose"))) return;
    try {
      await api.patch(`v1/performance/cycles/${id}/close`);
      toast.success(t("performance.cycles.toasts.closed"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("performance.cycles.confirmDeleteDraft"))) return;
    try {
      await api.delete(`v1/performance/cycles/${id}/`);
      toast.success(t("performance.cycles.toasts.deleted"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold" style={{ color: "var(--lm-ink)" }}>{t("performance.cycles.title")}</h3>
        <Button size="sm" onClick={() => setShowForm(!showForm)}>
          <Plus className="h-4 w-4 mr-1.5" />
          {t("performance.cycles.new")}
        </Button>
      </div>

      {/* Create form */}
      {showForm && (
        <div
          className="rounded-xl p-5 space-y-4"
          style={{ backgroundColor: "var(--lm-s2)", border: "1px solid #0ea5e9" }}
        >
          <h4 className="font-semibold text-sm" style={{ color: "var(--lm-ink)" }}>{t("performance.cycles.createTitle")}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium" style={{ color: "var(--lm-ink-2)" }}>{t("performance.cycles.fieldName")}</label>
              <Input
                placeholder={t("performance.cycles.namePlaceholder")}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium" style={{ color: "var(--lm-ink-2)" }}>{t("performance.cycles.fieldType")}</label>
              <select
                className="w-full rounded-md border px-3 py-2 text-sm"
                style={{ backgroundColor: "var(--lm-canvas)", borderColor: "var(--lm-border)", color: "var(--lm-ink)" }}
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
              >
                <option value="annual">{t("performance.cycles.type.annual")}</option>
                <option value="probation">{t("performance.cycles.type.probation")}</option>
                <option value="continuous">{t("performance.cycles.type.continuous")}</option>
                <option value="custom">{t("performance.cycles.type.custom")}</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium" style={{ color: "var(--lm-ink-2)" }}>{t("performance.cycles.fieldStartDate")}</label>
              <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium" style={{ color: "var(--lm-ink-2)" }}>{t("performance.cycles.fieldEndDate")}</label>
              <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
            </div>
            <div className="md:col-span-2 space-y-1">
              <label className="text-xs font-medium" style={{ color: "var(--lm-ink-2)" }}>{t("performance.cycles.fieldDescription")}</label>
              <Input
                placeholder={t("performance.cycles.descPlaceholder")}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
          </div>

          <p className="text-xs" style={{ color: "var(--lm-ink-3)" }}>
            {t("performance.cycles.preconfigNote", { scale: DEFAULT_SCALE.length, questions: DEFAULT_QUESTIONS.length })}
          </p>

          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => setShowForm(false)}>{t("common.cancel")}</Button>
            <Button size="sm" onClick={handleCreate} disabled={submitting}>
              {submitting ? t("performance.cycles.creating") : t("performance.cycles.createCta")}
            </Button>
          </div>
        </div>
      )}

      {/* Cycles list */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin h-6 w-6 rounded-full border-b-2" style={{ borderColor: "#0ea5e9" }} />
        </div>
      ) : cycles.length === 0 ? (
        <div
          className="rounded-xl p-8 text-center"
          style={{ backgroundColor: "var(--lm-s2)", border: "1px dashed var(--lm-border)" }}
        >
          <p className="text-sm" style={{ color: "var(--lm-ink-2)" }}>
            {t("performance.cycles.emptyPre")}<strong>{t("performance.cycles.new")}</strong>{t("performance.cycles.emptyPost")}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {cycles.map((cycle) => (
            <div
              key={cycle.id}
              className="rounded-xl overflow-hidden"
              style={{ backgroundColor: "var(--lm-s2)", border: "1px solid var(--lm-border)" }}
            >
              <div className="flex items-center justify-between px-4 py-3.5">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <StatusDot status={cycle.status} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate" style={{ color: "var(--lm-ink)" }}>{cycle.name}</p>
                    <p className="text-xs" style={{ color: "var(--lm-ink-2)" }}>
                      {cycle.start_date} → {cycle.end_date} · {cycle.type}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 ml-3">
                  <span className="text-xs hidden sm:inline" style={{ color: "var(--lm-ink-2)" }}>
                    {cycle.completed_reviews}/{cycle.total_reviews} {t("performance.dashboard.evals")}
                  </span>
                  <CycleStatusBadge status={cycle.status} t={t} />

                  {cycle.status === "draft" && (
                    <button
                      onClick={() => handleActivate(cycle.id)}
                      className="p-1.5 rounded-md hover:opacity-80 transition-opacity"
                      title={t("performance.cycles.activateTitle")}
                      style={{ backgroundColor: "rgba(34,197,94,0.12)", color: "#22c55e" }}
                    >
                      <Play className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {cycle.status === "active" && (
                    <button
                      onClick={() => handleClose(cycle.id)}
                      className="p-1.5 rounded-md hover:opacity-80 transition-opacity"
                      title={t("performance.cycles.closeTitle")}
                      style={{ backgroundColor: "rgba(148,163,184,0.12)", color: "#94a3b8" }}
                    >
                      <CheckCheck className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {cycle.status === "draft" && (
                    <button
                      onClick={() => handleDelete(cycle.id)}
                      className="p-1.5 rounded-md hover:opacity-80 transition-opacity"
                      title={t("performance.cycles.deleteTitle")}
                      style={{ backgroundColor: "rgba(239,68,68,0.12)", color: "#ef4444" }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button
                    onClick={() => setExpandedCycle(expandedCycle === cycle.id ? null : cycle.id)}
                    className="p-1.5 rounded-md"
                    style={{ color: "var(--lm-ink-3)" }}
                  >
                    {expandedCycle === cycle.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {expandedCycle === cycle.id && (
                <div
                  className="px-4 pb-4 pt-1 text-xs space-y-2"
                  style={{ borderTop: "1px solid var(--lm-border)", color: "var(--lm-ink-2)" }}
                >
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <span className="font-medium">{t("performance.cycles.scaleLabel")}</span>{" "}
                      {(cycle.rating_scale || []).map((s) => s.label).join(", ") || t("performance.cycles.notConfigured")}
                    </div>
                    <div>
                      <span className="font-medium">{t("performance.cycles.questionsLabel")}</span>{" "}
                      {t("performance.cycles.questionsConfigured", { count: (cycle.questions || []).length })}
                    </div>
                    <div>
                      <span className="font-medium">{t("performance.cycles.salaryRulesLabel")}</span>{" "}
                      {t("performance.cycles.tiers", { count: (cycle.salary_rules || []).length })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusDot({ status }) {
  const color = status === "active" ? "#22c55e" : status === "closed" ? "#94a3b8" : "#f59e0b";
  return <span className="h-2 w-2 rounded-full inline-block flex-shrink-0" style={{ backgroundColor: color }} />;
}

function CycleStatusBadge({ status, t }) {
  const styles = {
    draft:  { bg: "rgba(245,158,11,0.12)",  color: "#f59e0b", label: t("performance.status.draft") },
    active: { bg: "rgba(34,197,94,0.12)",   color: "#22c55e", label: t("performance.status.active") },
    closed: { bg: "rgba(148,163,184,0.12)", color: "#94a3b8", label: t("performance.status.closed") },
  };
  const s = styles[status] || styles.draft;
  return (
    <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}
